import { IChecklistItemRow } from "@mctypes/checklist.types";
import { RowDragEndEvent, RowDragEnterEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useRef } from "react";
import { usePostProjectByProjectIdChecklistUpdateRowPositionMutation } from "../../../../../openapi";
import { useAppDispatch } from "../../../../hooks";
import { setRefactoredCollapsedCategoryIdsInRedux, setRefactoredExpandedCategoryIdsInRedux } from "../checklistSlice";
import { getChecklistDragTargetCategoryIndex, getChecklistRowMoveInfo } from "./checklistTableRefactorUtils";

type ChecklistCategoryCollapseState = {
  allCategoriesCollapsed: boolean;
  categoryIds: string[];
  collapsedCategoryIds: Set<string>;
};

type UseChecklistRowDragProps = {
  checklistObjectId?: string | null;
  collapseState: ChecklistCategoryCollapseState;
  gridRef: RefObject<AgGridReact<IChecklistItemRow>>;
  projectId?: string | null;
  refactoredCollapsedCategoryIds: string[];
  refactoredExpandedCategoryIds: string[];
  rows: IChecklistItemRow[];
};

export const useChecklistRowDrag = ({
  checklistObjectId,
  collapseState,
  gridRef,
  projectId,
  refactoredCollapsedCategoryIds,
  refactoredExpandedCategoryIds,
  rows,
}: UseChecklistRowDragProps) => {
  const dispatch = useAppDispatch();
  const categoryDragCollapseStateRef = useRef<{
    collapsedCategoryIds: string[];
    expandedCategoryIds: string[];
  } | null>(null);
  const [updateRowPositionMutation] = usePostProjectByProjectIdChecklistUpdateRowPositionMutation();

  const restoreCategoryDragCollapseState = useCallback(() => {
    const previousCollapseState = categoryDragCollapseStateRef.current;
    if (previousCollapseState == null) {
      return;
    }

    dispatch(setRefactoredCollapsedCategoryIdsInRedux(previousCollapseState.collapsedCategoryIds));
    dispatch(setRefactoredExpandedCategoryIdsInRedux(previousCollapseState.expandedCategoryIds));
    categoryDragCollapseStateRef.current = null;
  }, [dispatch]);

  const onRowDragEnter = useCallback(
    (event: RowDragEnterEvent<IChecklistItemRow>) => {
      const draggedRow = event.node.data;
      if (draggedRow?.originalItem != null || categoryDragCollapseStateRef.current != null) {
        return;
      }

      categoryDragCollapseStateRef.current = {
        collapsedCategoryIds: refactoredCollapsedCategoryIds,
        expandedCategoryIds: refactoredExpandedCategoryIds,
      };

      dispatch(
        setRefactoredCollapsedCategoryIdsInRedux([
          ...new Set([...refactoredCollapsedCategoryIds, ...collapseState.categoryIds]),
        ]),
      );
      dispatch(
        setRefactoredExpandedCategoryIdsInRedux(
          refactoredExpandedCategoryIds.filter(
            (categoryId) => collapseState.categoryIds.includes(categoryId) === false,
          ),
        ),
      );
    },
    [collapseState.categoryIds, dispatch, refactoredCollapsedCategoryIds, refactoredExpandedCategoryIds],
  );

  const onRowDragEnd = useCallback(
    async (event: RowDragEndEvent<IChecklistItemRow>) => {
      const draggedRow = event.node.data;
      const api = gridRef.current?.api;
      const newPosition = event.node.rowIndex;

      if (draggedRow == null || api == null || newPosition == null || projectId == null || checklistObjectId == null) {
        restoreCategoryDragCollapseState();
        return;
      }

      const isCat = draggedRow.originalItem == null;
      const isItem = draggedRow.originalItem != null;

      if (isItem === true && newPosition === 0) {
        api.setGridOption("rowData", rows);
        restoreCategoryDragCollapseState();
        return;
      }

      const newCategoryIndex = getChecklistDragTargetCategoryIndex(api, newPosition);
      const newCategoryRow = api.getDisplayedRowAtIndex(newCategoryIndex)?.data;
      const originalIndex = rows.findIndex((row) => row._id === draggedRow._id);

      if (
        (isItem === true && newCategoryIndex === -1) ||
        originalIndex === newPosition ||
        event.overNode?.data == null
      ) {
        restoreCategoryDragCollapseState();
        return;
      }

      try {
        await updateRowPositionMutation({
          projectId,
          body: getChecklistRowMoveInfo({
            draggedRow,
            newPosition,
            newCategoryIndex,
            newCategoryRow,
            checklistId: checklistObjectId,
          }),
        }).unwrap();
      } catch {
        api.setGridOption("rowData", rows);
      } finally {
        restoreCategoryDragCollapseState();
      }
    },
    [
      checklistObjectId,
      dispatch,
      gridRef,
      projectId,
      restoreCategoryDragCollapseState,
      rows,
      updateRowPositionMutation,
    ],
  );

  return {
    onRowDragEnd,
    onRowDragEnter,
    restoreCategoryDragCollapseState,
  };
};
